import { useEffect, useRef } from "react";
import {
  clearDiscordPresence,
  ensureDiscordSdkReady,
  getDiscordSdk,
} from "./sdk";
import { isDiscordFrame } from "../utils/frame";
import { useSnapshot } from "../data/store";
import { fmtCompact, toNum } from "../game/format";
import type { Snapshot } from "../game/types";

// Rich Presence art asset key. Upload
//   apps/web/public/icons/cookie.webp
// (512px 🍪, source: https://em-content.zobj.net/source/telegram/386/cookie_1f36a.webp)
// in the Discord developer portal under Rich Presence Art Assets as `cookie`.
// Small status assets `golden` (🌟) and `rush` (🔥) must likewise exist in the
// portal; when missing, Discord simply omits the small image and the rest of
// the activity still renders.
export const PRESENCE_LARGE_IMAGE = "cookie";
export const PRESENCE_SMALL_GOLDEN = "golden";
export const PRESENCE_SMALL_RUSH = "rush";

// Min interval between setActivity calls. Combined with the only-on-change
// key below this guarantees no per-click spam: snapshots arrive on every
// game tick / refresh / reconnect, but presence updates at most once per 30s
// and only when the visible payload actually changed.
const PRESENCE_THROTTLE_MS = 30_000;

// Session start for timestamps.start. Module-level so WS reconnects,
// provider remounts (retry), and token refreshes keep one stable "elapsed"
// clock instead of restarting it.
let presenceStart = 0;

function getPresenceStart(now: number): number {
  if (!presenceStart) presenceStart = now;
  return presenceStart;
}

/** Live baker count: authoritative snapshot field first, players fallback. */
function activeBakersOf(snap: Snapshot): number {
  const fromBakery = snap.bakery.activeBakers;
  if (
    typeof fromBakery === "number" &&
    Number.isFinite(fromBakery) &&
    fromBakery > 0
  ) {
    return Math.floor(fromBakery);
  }
  return (snap.players ?? []).filter((p) => p.active).length;
}

function eventLive(snap: Snapshot, id: string, now: number): boolean {
  const ev = (snap.events ?? []).find((e) => e.id === id) ?? null;
  return !!ev && ev.endsAt > now;
}

interface PresencePayload {
  details: string;
  state: string;
  level: number;
  goldenOn: boolean;
  rushOn: boolean;
}

function buildPresence(snap: Snapshot, now: number): PresencePayload {
  const lifetime = toNum(snap.bakery.lifetimeCookies ?? snap.bakery.cookies);
  const level = snap.bakery.level ?? 1;
  const details = `🍪 ${fmtCompact(lifetime)} baked · Bakery Lv ${level}`;
  const cps = toNum(snap.bakery.cps);
  const bakers = activeBakersOf(snap);
  const state = `+${fmtCompact(cps)}/s · ${bakers} baker${bakers === 1 ? "" : "s"}`;
  return {
    details,
    state,
    level,
    goldenOn: eventLive(snap, "golden_cookie", now),
    rushOn: eventLive(snap, "cookie_rush", now),
  };
}

/**
 * Discord Rich Presence via Embedded App SDK setActivity.
 *
 * - Frame only (no-op in browser).
 * - Runs on EVERY snapshot (stored JWT/me success, token refresh, WS
 *   reconnect, live ticks) — not only right after authorize. Snapshots are
 *   the trigger; the shared SDK singleton resolves the transport in the same
 *   tick (ready handshake + background authorize warm), so stored-auth paths
 *   send presence too instead of silently returning with a null SDK.
 * - details = lifetime + bakery level, state = cps + bakers (no party).
 * - type 0 (Playing), large_image = cookie, large_text = Bakery Lv,
 *   small_image = golden/rush status, timestamps.start set once per session.
 * - Throttled to 1 update / 30s and only when the payload changed.
 * - Behind the "Share Discord status" settings toggle (default on);
 *   opt-out clears the activity.
 * - Never throws: every SDK call is wrapped in try/catch so the game
 *   keeps running when presence fails.
 */
export function useDiscordPresence(snap: Snapshot | null): void {
  const { state } = useSnapshot();
  const enabled = state.settings.discordPresence !== false;
  const lastSentRef = useRef<{ key: string; at: number }>({ key: "", at: 0 });

  useEffect(() => {
    if (!enabled) {
      // Opt-out: clear any previously set activity, best-effort.
      lastSentRef.current = { key: "", at: 0 };
      try {
        void clearDiscordPresence().catch(() => undefined);
      } catch {
        return;
      }
      return;
    }
    if (!snap) return;
    let inFrame = false;
    try {
      inFrame = isDiscordFrame();
    } catch {
      return;
    }
    if (!inFrame) return;
    try {
      const now = Date.now();
      const p = buildPresence(snap, now);
      const smallImage = p.goldenOn
        ? PRESENCE_SMALL_GOLDEN
        : p.rushOn
          ? PRESENCE_SMALL_RUSH
          : undefined;
      const smallText = p.goldenOn
        ? "Golden Frenzy"
        : p.rushOn
          ? "Cookie Rush"
          : undefined;
      // No party: baker count lives in state text only (no fake size max).
      const activity = {
        type: 0,
        details: p.details,
        state: p.state,
        timestamps: { start: getPresenceStart(now) },
        assets: {
          large_image: PRESENCE_LARGE_IMAGE,
          large_text: `Bakery Lv ${p.level}`,
          ...(smallImage ? { small_image: smallImage } : {}),
          ...(smallText ? { small_text: smallText } : {}),
        },
      };
      const key = JSON.stringify(activity);
      if (key === lastSentRef.current.key) return;
      if (now - lastSentRef.current.at < PRESENCE_THROTTLE_MS) return;
      // Resolve the shared SDK in the same tick so stored-auth paths
      // (cookie/JWT + /me, refresh, reconnect) send presence instead of
      // silently returning when the fresh-authorize flow was skipped.
      void (async () => {
        try {
          const sdk = getDiscordSdk() ?? (await ensureDiscordSdkReady());
          if (!sdk) return;
          await sdk.commands.setActivity({ activity });
          lastSentRef.current = { key, at: Date.now() };
        } catch {
          return;
        }
      })().catch(() => undefined);
    } catch {
      return;
    }
  }, [snap, enabled]);

  // Clear presence on unmount (best-effort, never throws).
  useEffect(() => {
    return () => {
      try {
        void clearDiscordPresence().catch(() => undefined);
      } catch {
        return;
      }
    };
  }, []);
}
